import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Lightbulb, Video, Send } from "lucide-react";
import { Button } from "../ui/button";

const steps = [
  {
    number: "01",
    icon: Lightbulb,
    title: "Estrategia",
    description: "Definimos objetivos, audiencia y formato. Planificamos el calendario de contenido para que nunca te quedes sin ideas.",
    gradient: "from-blue-500 to-cyan-500",
    ring: "ring-blue-100",
  },
  {
    number: "02",
    icon: Video,
    title: "Grabación Premium",
    description: "Producción multicámara 4K en nuestro estudio o en tu oficina. Tú solo hablas, nosotros nos encargamos de todo.",
    gradient: "from-purple-500 to-pink-500",
    ring: "ring-purple-100",
  },
  {
    number: "03",
    icon: Send,
    title: "Distribución",
    description: "Editamos, creamos clips para redes y te entregamos todo listo para publicar en YouTube, Spotify y más.",
    gradient: "from-pink-500 to-red-500",
    ring: "ring-pink-100",
  },
];

export default function ProcessMethod() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="proceso"
      className="section-spacing bg-white relative overflow-hidden"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-purple font-body font-semibold mb-4">
            Proceso
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            De la idea al aire en
            <span className="gradient-text"> 3 pasos</span>
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Un sistema probado que elimina la complejidad. Tú te centras en tu mensaje, nosotros en todo lo demás.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="relative group"
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-gradient-to-r from-slate-200 to-transparent z-0" />
              )}

              <div className="relative h-full p-8 rounded-2xl bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                {/* Number */}
                <span className={`font-heading font-extrabold text-6xl bg-gradient-to-r ${step.gradient} bg-clip-text text-transparent opacity-20 absolute top-4 right-6`}>
                  {step.number}
                </span>

                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${step.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <step.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="font-heading font-bold text-xl text-galvani-text mb-3">
                  {step.title}
                </h3>
                <p className="font-body text-galvani-textLight text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <p className="font-body text-galvani-textLight mb-6">
            Un proceso claro, sin sorpresas y con resultados medibles
          </p>
          <a href="#solicitar-propuesta">
            <Button
              className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-8 py-4 text-base rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/25"
              data-testid="process-cta"
            >
              Solicitar propuesta
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
